import React, { useEffect, useState } from 'react';
import { Field, inputClass } from './BackgroundFields';
import {
  fetchCampaignWithProducts,
  fetchCampaignsForPicker,
  formatProductPriceEur,
  productPickerPatch,
  refreshCampaignProductProps,
  resolveProductDisplay,
} from '../utils/campaignWebCreator';

export default function CampaignProductFields({ props, onPatch }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const inheritTitle = props.inheritTitle !== false;
  const inheritDescription = props.inheritDescription !== false;
  const display = resolveProductDisplay(props);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCampaignsForPicker()
      .then((list) => {
        if (!cancelled) setCampaigns(list);
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger les campagnes.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCampaign = campaigns.find((c) => c.id === String(props.campaignId || '')) || null;
  const products = selectedCampaign?.products || [];
  const selectedProduct = products.find((p) => p.id === String(props.productId || '')) || null;

  const handleCampaignChange = async (campaignId) => {
    if (!campaignId) {
      onPatch({
        campaignId: '',
        productId: '',
        price: null,
        stancerConfigured: false,
        productName: '',
        productDescription: '',
      });
      return;
    }
    try {
      const campaign = await fetchCampaignWithProducts(campaignId);
      if (!campaign) return;
      const firstProduct = campaign.products[0];
      if (!firstProduct) {
        onPatch({
          campaignId: campaign.id,
          productId: '',
          price: null,
          stancerConfigured: campaign.stancerConfigured,
          productName: '',
          productDescription: '',
        });
        return;
      }
      onPatch(productPickerPatch(campaign, firstProduct, props));
    } catch {
      setError('Impossible de charger la campagne.');
    }
  };

  const handleProductChange = (productId) => {
    if (!selectedCampaign) return;
    const product = selectedCampaign.products.find((p) => p.id === productId);
    if (!product) return;
    onPatch(productPickerPatch(selectedCampaign, product, props));
  };

  const handleRefreshFromCampaign = async () => {
    try {
      const next = await refreshCampaignProductProps({ type: 'product', props });
      onPatch(next);
    } catch {
      setError('Impossible de rafraîchir depuis la campagne.');
    }
  };

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onPatch({ imageUrl: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <Field label="Campagne">
        <select
          className={inputClass}
          value={props.campaignId || ''}
          disabled={loading}
          onChange={(e) => handleCampaignChange(e.target.value)}
        >
          <option value="">— Choisir une campagne —</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {!c.stancerConfigured ? ' (Stancer non configuré)' : ''}
            </option>
          ))}
        </select>
      </Field>

      {props.campaignId ? (
        <Field label="Produit (prix campagne)">
          <select
            className={inputClass}
            value={props.productId || ''}
            onChange={(e) => handleProductChange(e.target.value)}
          >
            <option value="">— Choisir un produit —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {formatProductPriceEur(p.price)} TTC
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      {props.productId ? (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/20 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Prix lié</span>
          <span className="font-semibold text-foreground tabular-nums">
            {formatProductPriceEur(props.price)} TTC
          </span>
        </div>
      ) : null}

      {props.campaignId && !props.stancerConfigured ? (
        <p className="text-xs text-muted-foreground rounded-lg border border-border px-3 py-2">
          Paiement désactivé —{' '}
          <a href="/crm-configuration" className="text-primary underline">
            Stancer
          </a>
        </p>
      ) : null}

      {props.productId ? (
        <>
          <label className="flex items-start gap-3 rounded-lg border border-border p-3 bg-muted/10 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={inheritTitle}
              onChange={(e) =>
                onPatch({
                  inheritTitle: e.target.checked,
                  title: e.target.checked ? '' : props.title || props.productName || '',
                })
              }
            />
            <span>
              <span className="text-sm font-medium text-foreground block">
                Hériter du titre du produit campagne
              </span>
              <span className="text-xs text-muted-foreground">
                {inheritTitle
                  ? `Affiché : « ${props.productName || selectedProduct?.name || '—'} »`
                  : 'Saisissez un titre personnalisé ci-dessous.'}
              </span>
            </span>
          </label>

          {!inheritTitle ? (
            <Field label="Titre personnalisé">
              <input
                className={inputClass}
                value={props.title || ''}
                onChange={(e) => onPatch({ title: e.target.value, inheritTitle: false })}
                placeholder="Titre affiché sur la carte"
              />
            </Field>
          ) : null}

          <label className="flex items-start gap-3 rounded-lg border border-border p-3 bg-muted/10 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={inheritDescription}
              onChange={(e) =>
                onPatch({
                  inheritDescription: e.target.checked,
                  description: e.target.checked
                    ? ''
                    : props.description || props.productDescription || '',
                })
              }
            />
            <span>
              <span className="text-sm font-medium text-foreground block">
                Hériter de la description du produit campagne
              </span>
              <span className="text-xs text-muted-foreground line-clamp-2">
                {inheritDescription
                  ? props.productDescription || selectedProduct?.description || 'Aucune description campagne.'
                  : 'Saisissez une description personnalisée ci-dessous.'}
              </span>
            </span>
          </label>

          {!inheritDescription ? (
            <Field label="Description personnalisée">
              <textarea
                className={`${inputClass} min-h-[88px] resize-y`}
                value={props.description || ''}
                onChange={(e) =>
                  onPatch({ description: e.target.value, inheritDescription: false })
                }
                placeholder="Description affichée sur la carte"
              />
            </Field>
          ) : null}

          <div className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Aperçu texte : </span>
            {display.title}
            {display.description ? ` — ${display.description.slice(0, 80)}${display.description.length > 80 ? '…' : ''}` : ''}
          </div>
        </>
      ) : null}

      <Field label="Image (URL)">
        <input
          className={inputClass}
          value={props.imageUrl || ''}
          onChange={(e) => onPatch({ imageUrl: e.target.value })}
          placeholder="https://…"
        />
      </Field>

      <Field label="Importer une image">
        <input type="file" accept="image/*" onChange={handleImageFile} className="text-sm" />
      </Field>

      <Field label="Texte du bouton">
        <input
          className={inputClass}
          value={props.buttonLabel || 'Acheter'}
          onChange={(e) => onPatch({ buttonLabel: e.target.value })}
        />
      </Field>

      {props.productId ? (
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={handleRefreshFromCampaign}
        >
          Rafraîchir prix et textes campagne
        </button>
      ) : null}
    </div>
  );
}
